import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import { Container } from '@mui/system'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import AppRoutes from '../../../services/AppRoutes'
import UseTranslation from '../../../hooks/UseTranslation.jsx'
import TitleWithBackBtn from '../../../components/PageHelpers/TitleWithBackBtn.jsx'
import OfoqThemeBtn from '../../../components/Buttons/OfoqThemeBtn.jsx'
import { SAML_KEY } from '../../../utils/constants'
import { companiesResetAlertHandler, retrieveCompanyDetails } from '../../../redux/features/companies/companiesThunk'
import { selectCompanies } from '../../../redux/features/companies/companiesSlice'
import GeneralCompanyForm from './GeneralCompanyForm.jsx'
import SsoConfigurationStep from './SsoConfigurationStep.jsx'
import LoaderWithErrorHandler from '../../../components/PageHelpers/LoaderWithErrorHandler.jsx'

const TAB_GENERAL = 0
const TAB_SSO = 1
const SSO_MODE_ADD = 'add'
const SSO_MODE_EDIT = 'edit'
const hasCoreModuleSelected = (modules = []) => (
  modules?.some((module) => module?.id === 4 || module?.code === 'core') || false
)
const getStepCircleSx = (visualState) => ([
  styles.stepCircle,
  visualState === 'active' && styles.stepCircleActive,
  visualState === 'done' && styles.stepCircleDone,
  visualState === 'pending' && styles.stepCirclePending,
  visualState === 'locked' && styles.stepCircleLocked,
])

const getStepConnectorLineSx = (isProgress) => ([
  styles.stepConnectorLine,
  isProgress && styles.stepConnectorLineProgress,
])

const StepPrimaryLabel = ({ visualState, children }) => (
  <Typography
    sx={[
      styles.stepPrimaryLabel,
      visualState === 'active' && styles.stepPrimaryLabelActive,
      visualState === 'done' && styles.stepPrimaryLabelDone,
      visualState === 'pending' && styles.stepPrimaryLabelPending,
      visualState === 'locked' && styles.stepPrimaryLabelLocked,
    ]}
  >
    {children}
  </Typography>
)

const AddEditCompany = () => {
  const baseUri = process.env.REACT_APP_ROOT_ROUTE || ''
  const history = useHistory()
  const location = useLocation()
  const { companyId } = useParams()
  const generalFormRef = useRef(null)
  const ssoFormRef = useRef(null)
  const dispatch = useDispatch()
  const { t } = UseTranslation()
  const isXsScreen = useMediaQuery((theme) => theme.breakpoints.only('xs'))
  const companies = useSelector(selectCompanies)
  const companyDetails = companies?.companyDetails
  const isEdit = Boolean(companyId)
  const shouldOpenSsoFromState = Boolean(location?.state?.openSsoStep)
  const ssoStepModeFromState = location?.state?.ssoStepMode
  const createdCompanyIdFromState = location?.state?.createdCompanyId
  const createdCompanyNameFromState = location?.state?.createdCompanyName
  const [activeTab, setActiveTab] = useState(shouldOpenSsoFromState ? TAB_SSO : TAB_GENERAL)
  const [ssoTabUnlocked, setSsoTabUnlocked] = useState(isEdit || shouldOpenSsoFromState)
  const [ssoStepMode, setSsoStepMode] = useState(
    ssoStepModeFromState || (isEdit ? SSO_MODE_EDIT : SSO_MODE_ADD),
  )
  const [recentCompanyId, setRecentCompanyId] = useState(
    companyId ?? createdCompanyIdFromState ?? null,
  )
  const [recentCompanyName, setRecentCompanyName] = useState(createdCompanyNameFromState || '')
  const [isSsoEnabledChoice, setIsSsoEnabledChoice] = useState(true)
  const [canSubmitGeneral, setCanSubmitGeneral] = useState(!isEdit)
  const [canFinishSso, setCanFinishSso] = useState(false)

  const resolvedEditRow = useMemo(() => {
    if (!isEdit) return null
    if (String(companyDetails?.id) !== String(companyId)) return null
    const tenant = companyDetails?.tenant_list?.[0]
    if (!tenant) return null
    return {
      company: companyDetails,
      tenant,
      integration: tenant?.integration,
    }
  }, [isEdit, companyDetails, companyId])

  useEffect(() => {
    if (!isEdit || !companyId) return
    if (String(companyDetails?.id) === String(companyId)) return
    dispatch(retrieveCompanyDetails({ id: companyId }))
  }, [isEdit, companyId, companyDetails?.id, dispatch])

  const onGeneralSaved = useCallback(({
    companyId: savedId,
    mode,
    companyName,
  }) => {
    const hasSavedId = savedId !== undefined && savedId !== null
    const resolvedSsoMode = mode || (isEdit ? SSO_MODE_EDIT : SSO_MODE_ADD)
    setSsoStepMode(resolvedSsoMode)
    if (hasSavedId) setRecentCompanyId(String(savedId))
    setRecentCompanyName(companyName || '')
    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: {
        ...(location.state || {}),
        openSsoStep: true,
        ssoStepMode: resolvedSsoMode,
        createdCompanyId: hasSavedId
          ? String(savedId)
          : (location.state?.createdCompanyId ?? null),
        createdCompanyName: companyName || location.state?.createdCompanyName || '',
      },
    })
    setSsoTabUnlocked(true)
    setActiveTab(TAB_SSO)
  }, [history, isEdit, location.pathname, location.search, location.state])

  const pageTitle = isEdit
    ? (t?.['Edit Company'])
    : (t?.['Add New Company'])

  const primaryTab0Label = isEdit
    ? (t?.['Save & continue'])
    : (t?.['Create company & continue'])
  const primaryTab1Label = isEdit ? (t?.['Save changes']) : (t?.['Finish setup'])
  const isOnGeneralTab = activeTab === TAB_GENERAL
  const ssoPrimaryLabel = useMemo(() => {
    if (isSsoEnabledChoice) return primaryTab1Label
    return t?.['Finish setup']
  }, [isSsoEnabledChoice, primaryTab1Label, t])

  const goCompanies = useCallback(() => {
    history.push(`${baseUri}${AppRoutes.home}`)
  }, [baseUri, history])
  const goCompanyProfile = useCallback(() => {
    const targetCompanyId = companyId ?? recentCompanyId ?? createdCompanyIdFromState
    if (!targetCompanyId) {
      goCompanies()
      return
    }
    history.push(`${baseUri}${AppRoutes.companyProfile.replace(':companyId', String(targetCompanyId))}`)
  }, [baseUri, companyId, createdCompanyIdFromState, goCompanies, history, recentCompanyId])

  const handlePrimary = useCallback(async () => {
    if (isOnGeneralTab) {
      const generalSaved = await generalFormRef.current?.submitGeneralStep?.()
      if (generalSaved) return
      return
    }
    if (!isSsoEnabledChoice) {
      goCompanyProfile()
      return
    }
    const ssoSaved = await ssoFormRef.current?.submitSsoStep?.()
    if (ssoSaved) {
      goCompanyProfile()
    }
  }, [
    goCompanyProfile,
    isOnGeneralTab,
    isSsoEnabledChoice,
  ])

  const isSsoFinishBlocked = (
    !isOnGeneralTab &&
    isSsoEnabledChoice &&
    !canFinishSso
  )
  const isGeneralSubmitBlocked = isOnGeneralTab && !canSubmitGeneral
  const isPrimaryBlocked = isGeneralSubmitBlocked || isSsoFinishBlocked

  const canOpenSsoTab = ssoTabUnlocked
  const createdCompanyMemo = useMemo(() => ({
    id: companyId ?? recentCompanyId ?? createdCompanyIdFromState ?? null,
    name: recentCompanyName || createdCompanyNameFromState || '',
  }), [
    companyId,
    recentCompanyId,
    createdCompanyIdFromState,
    recentCompanyName,
    createdCompanyNameFromState,
  ])
  const resolvedCompanyId = createdCompanyMemo.id
  const resolvedCompanyName = resolvedEditRow?.company?.name || createdCompanyMemo.name || ''
  let backBtnTxt = `${t?.['Back to Companies']}`
  if (isXsScreen) backBtnTxt = `${t?.back}`
  if (isEdit) backBtnTxt = `${t?.['Back to']} ${resolvedCompanyName}`
  const canEnableSso = isEdit
    ? hasCoreModuleSelected(resolvedEditRow?.company?.modules)
    : true
  const shouldFetchExistingSsoConfig = (
    ssoStepMode === SSO_MODE_EDIT && isEdit && resolvedEditRow?.company?.sso_protocol === SAML_KEY
  )

  const goGeneral = useCallback(() => setActiveTab(TAB_GENERAL), [])
  const handleSsoChoiceChange = useCallback((isEnabled) => {
    setIsSsoEnabledChoice(Boolean(isEnabled))
  }, [])

  const { step1VisualState, step2VisualState } = useMemo(() => {
    const step1State = activeTab === TAB_GENERAL ? 'active' : 'done'

    let step2State = 'pending'
    if (!canOpenSsoTab) step2State = 'locked'
    else if (activeTab === TAB_SSO) step2State = 'active'

    return { step1VisualState: step1State, step2VisualState: step2State }
  }, [activeTab, canOpenSsoTab])

  return (
    <Container maxWidth="xl" sx={styles.container}>
       <TitleWithBackBtn
        title={
          <Typography variant='h5' sx={styles.companyTitle}>{pageTitle}</Typography>
        }
        backBtnTxt={backBtnTxt}
        onBack={isEdit ? goCompanyProfile : undefined}
        customStyle={styles.header}
      >
      <Box sx={styles.btnContainer}>
       <Box sx={styles.actionButtons}>
            <OfoqThemeBtn
              onClick={goCompanies} name={t?.Cancel} customStyle={styles.cancelBtn} cancelStyle>
            </OfoqThemeBtn>
            <OfoqThemeBtn
              onClick={handlePrimary}
              name={isOnGeneralTab ? primaryTab0Label : ssoPrimaryLabel}
              customStyle={styles.primaryBtn}
              disabled={isPrimaryBlocked}
              endIcon={isOnGeneralTab ? (
                <EastRoundedIcon sx={styles.primaryEndIcon} />
              ) : (
                <CheckRoundedIcon sx={styles.primaryEndIcon} />
              )}
            />
          </Box>
          </Box>
      </TitleWithBackBtn>
      <Box sx={styles.headerShell}>
          <Box sx={styles.stepperInner}>
            <Box
              component="div"
              sx={styles.stepSegment}
            >
              <Box
                sx={getStepCircleSx(step1VisualState)}
              >
                1
              </Box>
              <StepPrimaryLabel visualState={step1VisualState}>
                {t?.CompanyFormStepGeneral}
              </StepPrimaryLabel>
            </Box>

            <Box sx={styles.stepConnectorMobile} aria-hidden>
              <Box sx={getStepConnectorLineSx(step1VisualState === 'done')} />
            </Box>
            <Box sx={styles.stepConnectorWrap} aria-hidden>
              <Box sx={getStepConnectorLineSx(step1VisualState === 'done')} />
            </Box>

            <Box
              component="div"
              sx={[
                styles.stepSegment,
                styles.stepSegmentWide,
                !canOpenSsoTab && styles.stepButtonDisabled,
              ]}
            >
              <Box
                sx={getStepCircleSx(step2VisualState)}
              >
                2
              </Box>
              <Box sx={styles.stepTextBlock}>
                <StepPrimaryLabel visualState={step2VisualState}>
                  {t?.CompanyFormStepSso}
                </StepPrimaryLabel>
                {!ssoTabUnlocked && (
                  <Typography sx={styles.stepSecondaryHint}>
                    {t?.CompanyFormStepSsoHintShort}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
      </Box>

      <Box sx={styles.body}>
        {activeTab === TAB_GENERAL && (
              <GeneralCompanyForm
                key={isEdit ? String(companyId) : 'add'}
                ref={generalFormRef}
                isEdit={isEdit}
                initialRow={resolvedEditRow}
                onSaved={onGeneralSaved}
                onCanSubmitChange={setCanSubmitGeneral}
              />
        )}
        {activeTab === TAB_SSO && (
          <SsoConfigurationStep
            ssoRef={ssoFormRef}
            companyId={resolvedCompanyId}
            companyName={resolvedEditRow?.company?.name || createdCompanyMemo.name}
            mode={ssoStepMode}
            isEditFlow={ssoStepMode === SSO_MODE_EDIT}
            canEnableSso={canEnableSso}
            shouldFetchExistingSsoConfig={shouldFetchExistingSsoConfig}
            onSsoChoiceChange={handleSsoChoiceChange}
            onCanFinishSsoChange={setCanFinishSso}
            onEditDetails={goGeneral}
          />
        )}
      </Box>
      <LoaderWithErrorHandler
        store={companies}
        resetAlertHandler={companiesResetAlertHandler}
      />
    </Container>
  )
}

const styles = {
  container: {
    padding: { xs: '0px', sm: '0px 25px 0px 25px', md: '0px 52px !important' },
  },
  header: {
    height: { xs: 'auto', sm: '90px' },
    padding: { xs: '0px 0px 10px', sm: 'unset' },
    borderBottomLeftRadius: '15px',
    borderBottomRightRadius: '15px',
    margin: { xs: '-10px 10px 10px', sm: '-29px 0px 10px 0px', md: '-30px 0px 10px 0px' },
    '& > div': {
      marginTop: { xs: '15px', sm: '0px' },
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'column' },
      alignItems: { xs: 'flex-start', sm: 'unset' },
      px: { xs: '0px', sm: 'unset' },
      width: { xs: '100%', sm: 'unset' },

    },
    '& .mainTitle': {
      width: { xs: '95%', sm: 'unset' },
    },
    '& .backBtn': {
      minWidth: '20px',
      m: { xs: '0 5px 0 0', sm: '5px 0' },
    },
  },
  companyTitle: {
    fontSize: { xs: '16px', sm: '24px' },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: { xs: '150px', sm: '500px' },
  },
  headerShell: {
    backgroundColor: 'white',
    borderBottomLeftRadius: '15px',
    borderBottomRightRadius: '15px',
    px: { xs: 1, sm: 2, md: 0 },
    pt: { xs: '5px', sm: 2.5 },
    pb: { xs: 2, sm: 2 },
    mb: { xs: 2, sm: 3 },
    margin: { xs: '-15px 10px 10px', sm: '-29px 0px 10px 0px', md: '-30px 0px 10px 0px' },
  },
  actionButtons: {
    display: 'flex',
    flexDirection: { xs: 'row', sm: 'row' },
    flexWrap: { xs: 'nowrap', sm: 'nowrap' },
    alignItems: 'center',
    gap: { xs: '0px', sm: '5px' },
    width: { xs: '100%', sm: 'auto' },
    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
  },
  cancelBtn: {
    textTransform: 'capitalize',
    borderRadius: '10px',
    minWidth: { xs: '100px !important', sm: 'max-content' },
    px: { xs: 1, sm: 2.5 },
    py: { xs: 0.5, sm: 1 },
    fontFamily: 'Avenir-bold',
    fontSize: '0.875rem',
    color: 'primary.main',
    borderColor: 'primary.main',
    backgroundColor: '#fff',
    height: '40px',

  },
  primaryBtn: {
    height: '40px',
    textTransform: 'none',
    borderRadius: '10px',
    maxWidth: { xs: '100%', sm: 'max-content' },
    minWidth: { xs: 'max-content', sm: 'max-content' },
    px: { xs: 1, sm: 2.5 },
    py: { xs: 0.5, sm: 1 },
    fontFamily: 'Avenir-bold',
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
    backgroundColor: '#29CC8B',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#24b87d',
    },
    '&:disabled': {
      backgroundColor: '#29CC8B',
      opacity: 0.5,
      color: '#fff',
      cursor: 'not-allowed',
    },
  },
  primaryEndIcon: {
    fontSize: '1.125rem !important',
    color: 'inherit',
  },
  stepperInner: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: 0,
    width: '100%',
    p: { xs: '5px', sm: 2 },
    borderRadius: '12px',
    boxSizing: 'border-box',
  },
  stepSegment: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: '5px', sm: 1.5 },
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
    flexShrink: 0,
    minWidth: 0,
  },
  stepSegmentWide: {
    minWidth: 0,
    alignItems: { xs: 'center', sm: 'center' },
  },
  stepButtonDisabled: {
    cursor: 'not-allowed',
  },
  stepConnectorMobile: {
    display: 'none',
  },
  stepConnectorWrap: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 24,
    px: { xs: 1, sm: 1 },
    alignSelf: 'center',
  },
  stepConnectorLine: {
    width: '100%',
    height: 3,
    borderRadius: 2,
    backgroundColor: '#e4e7ec',
    transition: 'background-color 0.2s ease',
  },
  stepConnectorLineProgress: {
    backgroundColor: 'rgba(41, 204, 139, 0.45)',
  },
  stepCircle: {
    width: { xs: 30, sm: 36 },
    height: { xs: 30, sm: 36 },
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: { xs: '0.8125rem', sm: '0.9375rem' },
    fontWeight: 700,
    flexShrink: 0,
    fontFamily: 'Avenir',
    boxSizing: 'border-box',
    transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
  },
  stepCircleActive: {
    backgroundColor: '#29CC8B',
    color: '#fff',
    border: '2px solid #29CC8B',
    boxShadow: '0 2px 8px rgba(41, 204, 139, 0.35)',
  },
  stepCircleDone: {
    backgroundColor: '#29CC8B',
    color: '#fff',
    border: '2px solid #29CC8B',
  },
  stepCircleLocked: {
    backgroundColor: '#c5cad3',
    color: '#fff',
    border: '2px solid #b8bec8',
  },
  stepCirclePending: {
    backgroundColor: '#fff',
    color: '#696974',
    border: '2px solid #c5c8d0',
  },
  stepTextBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.25,
    minWidth: 0,
    textAlign: 'left',
  },
  stepPrimaryLabel: {
    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
    fontWeight: 700,
    fontFamily: 'Avenir',
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    color: '#1b2559',
    whiteSpace: 'nowrap',
  },
  stepPrimaryLabelActive: {
    color: '#29CC8B',
  },
  stepPrimaryLabelDone: {
    color: '#29CC8B',
  },
  stepPrimaryLabelPending: {
    color: '#696974',
    fontWeight: 600,
  },
  stepPrimaryLabelLocked: {
    color: '#92929d',
    fontWeight: 600,
  },
  stepSecondaryHint: {
    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
    fontWeight: 500,
    color: '#a4a4af',
    lineHeight: 1.35,
    maxWidth: 280,
  },
  body: {
    py: { xs: 1.5, sm: 2 },
    backgroundColor: '#f4f6f8',
    borderRadius: { xs: 0, sm: '12px' },
    px: { xs: 0, sm: 0.5 },
    minHeight: 320,
    mx: { xs: '10px', sm: 'auto' },
    width: { xs: 'calc(100% - 20px)', sm: '100%' },
    maxWidth: { xs: 'none', md: '900px' },
    boxSizing: 'border-box',
    overflowX: 'clip',
  },
  btnContainer: {
    position: { xs: 'fixed', sm: 'unset' },
    bottom: 0,
    zIndex: 100,
    right: 0,
    width: { xs: '97%', sm: 'fit-content' },
    border: { xs: '1px solid grey.200', sm: 'none' },
    backgroundColor: 'background.paper',
    padding: '10px',
    marginRight: { xs: '0px', sm: '0px' },
  },
}

export default AddEditCompany
